/**
 * Transform Prometheus matrix response to timeseries chart data
 */
export const transformMatrixToTimeSeries = (matrixResult) => {
  if (!matrixResult || matrixResult.length === 0) {
    return [];
  }

  const chartData = [];
  const timestamps = new Set();

  // Collect all unique timestamps
  matrixResult.forEach(series => {
    series.values?.forEach(([timestamp]) => timestamps.add(timestamp));
  });

  const sortedTimestamps = Array.from(timestamps).sort();

  // Build data points
  sortedTimestamps.forEach(timestamp => {
    const dataPoint = {
      time: new Date(timestamp * 1000).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      timestamp: timestamp
    };

    matrixResult.forEach((series, index) => {
      const value = series.values?.find(([ts]) => ts === timestamp)?.[1];

      // Get series name from metric labels
      const seriesName = series.metric?.dimension_FunctionName ||
        series.metric?.job ||
        series.metric?.__name__ ||
        `series${index + 1}`;

      dataPoint[seriesName] = value ? parseFloat(value) : null;
    });

    chartData.push(dataPoint);
  });

  return chartData;
};

/**
 * Transform Prometheus data for gauge visualization (latest single value)
 */
export const transformMatrixToGauge = (matrixResult) => {
  if (!matrixResult || matrixResult.length === 0) {
    return 0;
  }

  // Get the latest value from the first series
  const series = matrixResult[0];
  if (!series.values || series.values.length === 0) {
    return 0;
  }

  const latestValue = series.values[series.values.length - 1][1];
  return parseFloat(latestValue) || 0;
};

/**
 * Transform Prometheus data for pie chart (latest values per series)
 */
export const transformMatrixToPie = (matrixResult) => {
  if (!matrixResult || matrixResult.length === 0) {
    return [];
  }

  return matrixResult.map((series, index) => {
    const seriesName = series.metric?.dimension_FunctionName ||
      series.metric?.job ||
      series.metric?.__name__ ||
      `series${index + 1}`;

    // Get latest value
    const latestValue = series.values && series.values.length > 0
      ? parseFloat(series.values[series.values.length - 1][1])
      : 0;

    return {
      name: seriesName,
      value: latestValue
    };
  });
};

/**
 * Transform Prometheus data for stat visualization (aggregates)
 */
export const transformMatrixToStat = (matrixResult) => {
  if (!matrixResult || matrixResult.length === 0) {
    return { total: 0, average: 0, max: 0, min: 0, current: 0 };
  }

  const allValues = [];

  matrixResult.forEach(series => {
    series.values?.forEach(([_, value]) => {
      allValues.push(parseFloat(value));
    });
  });

  if (allValues.length === 0) {
    return { total: 0, average: 0, max: 0, min: 0, current: 0 };
  }

  const total = allValues.reduce((sum, val) => sum + val, 0);
  const average = total / allValues.length;
  const max = Math.max(...allValues);
  const min = Math.min(...allValues);

  // Get current (latest) value
  const lastSeries = matrixResult[matrixResult.length - 1];
  const current = lastSeries.values && lastSeries.values.length > 0
    ? parseFloat(lastSeries.values[lastSeries.values.length - 1][1])
    : 0;

  return { total, average, max, min, current };
};

/**
 * Transform Prometheus data for table visualization
 */
export const transformMatrixToTable = (matrixResult) => {
  if (!matrixResult || matrixResult.length === 0) {
    return [];
  }

  return matrixResult.map((series, index) => {
    const labels = series.metric || {};

    // Get latest value
    const latestValue = series.values && series.values.length > 0
      ? parseFloat(series.values[series.values.length - 1][1])
      : 0;

    return {
      ...labels, // Spread all labels as columns
      value: latestValue,
      _seriesIndex: index
    };
  });
};

/**
 * Main transformer - routes to appropriate transformer based on panel type
 */
export const transformPrometheusData = (matrixResult, panelType) => {
  switch (panelType) {
    case 'timeseries':
      return transformMatrixToTimeSeries(matrixResult);
    case 'gauge':
      return transformMatrixToGauge(matrixResult);
    case 'pie':
      return transformMatrixToPie(matrixResult);
    case 'stat':
      return transformMatrixToStat(matrixResult);
    case 'table':
      return transformMatrixToTable(matrixResult);
    default:
      return transformMatrixToTimeSeries(matrixResult);
  }
};

/**
 * Parse Grafana time strings (e.g., "now-6h") to unix timestamps
 */
export const parseGrafanaTimeToUnix = (timeString, referenceTime = Date.now()) => {
  if (timeString === 'now') {
    return Math.floor(referenceTime / 1000);
  }

  const match = timeString.match(/^now-(\d+)([smhdwMy])$/);
  if (!match) {
    // Try to parse as ISO string or unix timestamp
    const parsed = new Date(timeString).getTime();
    return isNaN(parsed) ? Math.floor(referenceTime / 1000) : Math.floor(parsed / 1000);
  }

  const [, amount, unit] = match;
  const value = parseInt(amount, 10);

  const unitMultipliers = {
    s: 1,
    m: 60,
    h: 3600,
    d: 86400,
    w: 604800,
    M: 2592000, // 30 days
    y: 31536000 // 365 days
  };

  const seconds = value * (unitMultipliers[unit] || 3600);
  return Math.floor(referenceTime / 1000) - seconds;
};